import React, { createContext, useContext, useState, useCallback } from "react";

export interface TransferCartItem {
  itemId: string;
  biblioId: string;
  biblioTitle: string;
  barcode: string;
  copyNumber: number;
  currentBranchId?: string;
  currentBranchName?: string;
  /** Destination branch — must be set before submitting */
  toBranchId: string;
  toBranchName: string;
  notes?: string;
}

interface TransferCartContextValue {
  items: TransferCartItem[];
  addItem: (item: TransferCartItem) => void;
  removeItem: (itemId: string) => void;
  updateItem: (itemId: string, patch: Partial<TransferCartItem>) => void;
  clearCart: () => void;
  isInCart: (itemId: string) => boolean;
  count: number;
}

const TransferCartContext = createContext<TransferCartContextValue | null>(null);

export function TransferCartProvider({ children }: { children: React.ReactNode }) {
  const [items, setItems] = useState<TransferCartItem[]>([]);

  const addItem = useCallback((item: TransferCartItem) => {
    setItems((prev) => {
      // Replace if already in cart (same itemId)
      const exists = prev.find((i) => i.itemId === item.itemId);
      if (exists) return prev.map((i) => (i.itemId === item.itemId ? item : i));
      return [...prev, item];
    });
  }, []);

  const removeItem = useCallback((itemId: string) => {
    setItems((prev) => prev.filter((i) => i.itemId !== itemId));
  }, []);

  const updateItem = useCallback((itemId: string, patch: Partial<TransferCartItem>) => {
    setItems((prev) => prev.map((i) => (i.itemId === itemId ? { ...i, ...patch } : i)));
  }, []);

  const clearCart = useCallback(() => setItems([]), []);

  const isInCart = useCallback((itemId: string) => items.some((i) => i.itemId === itemId), [items]);

  return (
    <TransferCartContext.Provider
      value={{ items, addItem, removeItem, updateItem, clearCart, isInCart, count: items.length }}
    >
      {children}
    </TransferCartContext.Provider>
  );
}

export function useTransferCart() {
  const ctx = useContext(TransferCartContext);
  if (!ctx) throw new Error("useTransferCart must be used within TransferCartProvider");
  return ctx;
}
